import axios from "axios";
import ResponseAlerts from "../functions/ResponseAlerts";

const BASE_ACCESS_MODULE = "access-control-service/access-modules";

const RESOURCES = {
	LIST_PAGINATED: (
		page,
		searchBy,
		condominiumId,
		licenceId,
		orderBy,
		orderDirection,
	) =>
		`${BASE_ACCESS_MODULE}/condominiums/${condominiumId}/licences/${licenceId}/paginated` +
		`?page=${page}&limit=8&searchBy=${searchBy}` +
		`&orderBy=${orderBy}&orderDirection=${orderDirection}`,
	LIST_BY_CONDOMINIUM_AND_LICENCE: (condominiumId, licenceId) =>
		`${BASE_ACCESS_MODULE}/condominiums/${condominiumId}/licences/${licenceId}`,
	LIST_BY_CONDOMINIUM: (condominiumId) =>
		`${BASE_ACCESS_MODULE}/condominiums/${condominiumId}`,
	LIST_BY_LICENCES: () =>
		`${BASE_ACCESS_MODULE}/licences`,
	LIST_LAST_MODULE_STATES_BY_CONDOMINIUM: (condominiumId) =>
		`access-control-service/access-modules-states-aggregate/last/condominiums/${condominiumId}`,
	LIST_MODULE_STATES_BY_CONDOMINIUM: (condominiumId) =>
		`access-control-service/access-modules-states-aggregate/condominiums/${condominiumId}`,
	GET_ONE: (id) => `${BASE_ACCESS_MODULE}/${id}`,
	GET_ONE_UUID: (uuid) => `${BASE_ACCESS_MODULE}/uuid/${uuid}`,
	GET_BY_UUIDS: () => `${BASE_ACCESS_MODULE}/uuids`,
	CREATE: () => `${BASE_ACCESS_MODULE}`,
	UPDATE: (id) => `${BASE_ACCESS_MODULE}/${id}`,
	DELETE: (id) => `${BASE_ACCESS_MODULE}/${id}`,
};

class AccessModuleService {
	static async listPaginated(
		page,
		searchBy,
		condominiumId,
		licenceId,
		orderBy,
		orderDirection,
		cancelToken,
	) {
		try {
			return await axios.get(
				RESOURCES.LIST_PAGINATED(
					page,
					searchBy,
					condominiumId,
					licenceId,
					orderBy,
					orderDirection,
				),
				{
					signal: cancelToken,
				}
			);
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar módulos de acesso",
			});
			return error.response;
		}
	}

	static async listByCondominiumAndLicence(condominiumId, licenceId) {
		try {
			return await axios.get(
				RESOURCES.LIST_BY_CONDOMINIUM_AND_LICENCE(condominiumId, licenceId),
			);
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage:
					"Erro ao listar módulos de acesso por condomínio e licença",
			});
			return error.response;
		}
	}

	static async listByLicences(licenseIds) {
		try {
			return await axios.post(
				RESOURCES.LIST_BY_LICENCES(),
				{ licenseIds }
			);
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage:
					"Erro ao listar módulos de acesso por licenças",
			});
			return error.response;
		}
	}

	static async listByCondominium(condominiumId, cancelToken = null) {
		try {
			return await axios.get(RESOURCES.LIST_BY_CONDOMINIUM(condominiumId), {
				signal: cancelToken,
			});
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar módulos de acesso por condomínio",
			});
			return error.response;
		}
	}

	static async listLastStatesByCondominium(condominiumId, cancelToken) {
		try {
			return await axios.get(RESOURCES.LIST_LAST_MODULE_STATES_BY_CONDOMINIUM(condominiumId), {
				signal: cancelToken,
			});
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar últimos estados dos módulos de acesso",
			});
			return error.response;
		}
	}

	static async listStatesByCondominium(condominiumId, filter) {
		try {
			return await axios.post(RESOURCES.LIST_MODULE_STATES_BY_CONDOMINIUM(condominiumId), filter);
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar estados dos módulos de acesso",
			});
			return error.response;
		}
	}

	static async getOne(id) {
		try {
			return await axios.get(RESOURCES.GET_ONE(id));
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao obter módulo de acesso",
			});
			return error.response;
		}
	}

	static async getOneUuid(uuid) {
		try {
			return await axios.get(RESOURCES.GET_ONE_UUID(uuid));
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao obter módulo de acesso",
			});
			return error.response;
		}
	}

	static async getByUuids(uuids) {
		try {
			return await axios.post(RESOURCES.GET_BY_UUIDS(), { uuids });
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao obter módulos de acessos",
			});
			return error.response;
		}
	}

	static async createAccessModule(payload) {
		try {
			const response = await axios.post(RESOURCES.CREATE(), payload);
			ResponseAlerts({
				response,
				successMessage: "Módulo de acesso cadastrado com sucesso",
			});
			return response;
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao cadastrar módulo de acesso",
			});
			return error.response;
		}
	}

	static async updateAccessModule(id, payload) {
		try {
			const response = await axios.put(RESOURCES.UPDATE(id), payload);
			ResponseAlerts({
				response,
				successMessage: "Módulo de acesso atualizado com sucesso",
			});
			return response;
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao atualizar módulo de acesso",
			});
			return error.response;
		}
	}

	static async deleteAccessModule(id) {
		try {
			const response = await axios.delete(RESOURCES.DELETE(id));
			ResponseAlerts({
				response,
				successMessage: "Módulo de acesso excluído com sucesso",
			});
			return response;
		} catch (error) {
			console.error(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao excluir módulo de acesso",
			});
			return error.response;
		}
	}
}

export default AccessModuleService;
